import { Suspense, lazy } from "react"
import { Routes, Route } from "react-router-dom"
import { Layout } from "@/components/Layout"
import { PasswordGate } from "@/components/PasswordGate"
import { Skeleton } from "@/components/ui/skeleton"

// Lazy load pages for code splitting
const Overview = lazy(() => import("@/pages/Overview").then(m => ({ default: m.Overview })))
const PnL = lazy(() => import("@/pages/PnL").then(m => ({ default: m.PnL })))
const Buyers = lazy(() => import("@/pages/Buyers").then(m => ({ default: m.Buyers })))
const Geography = lazy(() => import("@/pages/Geography").then(m => ({ default: m.Geography })))
const Publishers = lazy(() => import("@/pages/Publishers").then(m => ({ default: m.Publishers })))
const ApiPublishers = lazy(() => import("@/pages/ApiPublishers").then(m => ({ default: m.ApiPublishers })))
const Leads = lazy(() => import("@/pages/Leads").then(m => ({ default: m.Leads })))
const ISpeedImport = lazy(() => import("@/pages/ISpeedImport").then(m => ({ default: m.ISpeedImport })))
const MotivatedLeadsRefunds = lazy(() => import("@/pages/MotivatedLeadsRefunds").then(m => ({ default: m.MotivatedLeadsRefunds })))
const RealEstateBeesRefunds = lazy(() => import("@/pages/RealEstateBeesRefunds").then(m => ({ default: m.RealEstateBeesRefunds })))
const UVXLeads = lazy(() => import("@/pages/UVXLeads").then(m => ({ default: m.UVXLeads })))
const Stack = lazy(() => import("@/pages/Stack").then(m => ({ default: m.Stack })))
const StateDetail = lazy(() => import("@/pages/StateDetail").then(m => ({ default: m.StateDetail })))
const StateLeadsMap = lazy(() => import("@/pages/StateLeadsMap").then(m => ({ default: m.StateLeadsMap })))

// Loading fallback component
function PageLoader() {
  return (
    <div className="space-y-4 animate-in">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-6 w-32 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-8 w-32" />
      </div>
      <div className="grid grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Skeleton className="col-span-2 h-72 rounded-lg" />
        <Skeleton className="h-72 rounded-lg" />
      </div>
    </div>
  )
}

function App() {
  return (
    <PasswordGate>
      <Routes>
        {/* Standalone full-screen pages (no sidebar) */}
        <Route
          path="map/:state"
          element={
            <Suspense fallback={<div className="h-screen w-screen bg-background" />}>
              <StateLeadsMap />
            </Suspense>
          }
        />

        {/* Dashboard pages with sidebar */}
        <Route path="/" element={<Layout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoader />}>
              <Overview />
            </Suspense>
          }
        />
        <Route
          path="pnl"
          element={
            <Suspense fallback={<PageLoader />}>
              <PnL />
            </Suspense>
          }
        />
        <Route
          path="buyers"
          element={
            <Suspense fallback={<PageLoader />}>
              <Buyers />
            </Suspense>
          }
        />
        <Route
          path="geography"
          element={
            <Suspense fallback={<PageLoader />}>
              <Geography />
            </Suspense>
          }
        />
        <Route
          path="geography/:state"
          element={
            <Suspense fallback={<PageLoader />}>
              <StateDetail />
            </Suspense>
          }
        />
        <Route
          path="publishers"
          element={
            <Suspense fallback={<PageLoader />}>
              <Publishers />
            </Suspense>
          }
        />
        <Route
          path="api-publishers"
          element={
            <Suspense fallback={<PageLoader />}>
              <ApiPublishers />
            </Suspense>
          }
        />
        <Route
          path="leads"
          element={
            <Suspense fallback={<PageLoader />}>
              <Leads />
            </Suspense>
          }
        />
        <Route
          path="ispeed-import"
          element={
            <Suspense fallback={<PageLoader />}>
              <ISpeedImport />
            </Suspense>
          }
        />
        <Route
          path="motivatedleads-refunds"
          element={
            <Suspense fallback={<PageLoader />}>
              <MotivatedLeadsRefunds />
            </Suspense>
          }
        />
        <Route
          path="realestatebees-refunds"
          element={
            <Suspense fallback={<PageLoader />}>
              <RealEstateBeesRefunds />
            </Suspense>
          }
        />
        <Route
          path="uvx-leads"
          element={
            <Suspense fallback={<PageLoader />}>
              <UVXLeads />
            </Suspense>
          }
        />
        <Route
          path="stack"
          element={
            <Suspense fallback={<PageLoader />}>
              <Stack />
            </Suspense>
          }
        />
        </Route>
      </Routes>
    </PasswordGate>
  )
}

export default App
